import { setLanguage } from './fetch-api/fetch-api';
import getConfig, { setConfig } from './env.js';
import Store from './store/store';
import rootReducer from './store/reducer/root-reducer';
import Header from './components/header/header.js';
import Footer from './components/footer/footer.js';
import Sidebar from './components/sidebar/sidebar.js';
import { displayCookieDisabledInfo } from './components/cookieinfo/cookieinfo.js';
import { isArray, hexToRgb } from './utils/utils';
import styles from './css/static.css';
import setStyleTag from './utils/set-style-tag.js';
import {
  getCommonHeaderData,
  getUserCommonHeaderData,
  setCurrentApp,
  handleSessionExtend,
  reportingId
} from './api/api.js';
import {
  setIsUserToken,
  getIsUserToken,
  getParamLanguage,
  getIsEclientToken,
  getIsCookieDisabled
} from './fetch-api/lib/auth-token';

class App {
  static hasApplicationLanguageUpdated = ({
    applicationLanguage: { type = '', data = '' } = {}
  } = {}) => type === 'updated' && data !== '';

  static hasCurrentApplicationUpdated = ({
    currentApplication: { type = '', data = '' } = {}
  } = {}) => type === 'updated' && data !== '';

  static elementShouldExist = (queryString) => {
    const node = document.querySelector(queryString);
    if (node) {
      return node;
    }
    console.error(
      'element ',
      queryString,
      'should exist for Common Header to work properly',
    );
    return false;
  };

  static shouldUpdateLogo = ({ logo: { type = '', data = {} } = {} } = {}) =>
    type === 'updated' && !!data;

  static shouldUpdateGreeting = ({
    greeting: { type = '', data = {} } = {}
  } = {}) => type === 'updated' && !!data;

  static shouldUpdateClientColors = ({
    clientColors: { type = '', data } = {}
  } = {}) => type === 'updated' && !!data.primary && !!data.secondary;

  static shouldUpdateGlobalMenu = ({
    globalMenu: { type = '', data = '' } = {}
  } = {}) => type === 'updated' && isArray(data) && data.length > 0;

  static shouldUpdateContextMenu = ({
    contextMenu: { type = '', data = '' } = {}
  } = {}) => type === 'updated' && isArray(data);

  static shouldUpdateActivity = ({
    activity: { type = '', data = '' } = {}
  } = {}) => type === 'updated' && isArray(data);

  static shouldUpdateFooter = ({
    footer: { type = '', data = '' } = {}
  } = {}) => type === 'updated' && isArray(data);

  static shouldUpdateCartLink = ({
    cartLink: { type = '', data = '' } = {}
  } = {}) => type === 'updated' && !!data;

  static shouldUpdateCatalogLink = ({
    catalogLink: { type = '', data = '' } = {}
  } = {}) => type === 'updated' && !!data;

  static shouldUpdateTasksCount = ({
    tasksCount: { type = '', data = {} } = {}
  } = {}) => type === 'updated' && !!data;

  static shouldUpdateBank = ({ banks: { type = '', data = '' } = {} } = {}) =>
    type === 'updated' && isArray(data);

  static shouldUpdateCustomerName = ({
    customerName: { type = '', data = '' } = {}
  } = {}) => type === 'updated' && !!data;

  static shouldUpdateAwards = ({
    awards: { type = '', data = '' } = {}
  } = {}) => type === 'updated' && isArray(data);

  static shouldUpdateCatalogs = ({
    catalogs: { type = '', data = '' } = {}
  } = {}) => type === 'updated' && isArray(data);

  static shouldUpdateWidgetEnabled = ({
    hasGivenWidgetEnabled: { type = '', data = false } = {}
  } = {}) => type === 'updated' && !!data;

  static shouldUpdateGenericAlerts = ({
    genericAlerts: { type = '', data = '' } = {}
  } = {}) => type === 'updated' && isArray(data);

  static shouldUpdateRecieveAlerts = ({
    receiveAlerts: { type = '', data = '' } = {}
  } = {}) => type === 'updated' && isArray(data);

  static shouldUpdateAlertsCount = ({
    alertsCount: { type = '', data = {} } = {}
  } = {}) => type === 'updated' && !!data;

  static shouldUpdateTranslations = ({
    translations: { type = '', data = {} } = {}
  } = {}) => type === 'updated' && !!data;

  static shouldUpdateSessionExtendDialog = ({
    sessionExtendDialog: { type = '', data = {} } = {}
  } = {}) => type === 'updated' && !!data;

  static shouldUpdateRefreshAuthToken = ({
    refreshAuthToken: { type = '', data = {} } = {}
  } = {}) => type === 'updated' && !!data;

  static isValidResponse = ({
    logo = {},
    subscriptions = [],
    color: { primary = '', secondary = '' } = {}
  } = {}) =>
    logo &&
    !!logo.url &&
    subscriptions.length > 0 &&
    !!primary &&
    !!secondary !== '';

  static isValidResponseLayouts = ({
    theme: { primaryHexColor = '' } = {}
  } = {}) => !!primaryHexColor;

  // eslint-disable-next-line class-methods-use-this
  setHandleSessionExtend(dispatch) {
    handleSessionExtend(dispatch, false);
  }

  setApplicationLanguage(x) {
    setLanguage(x);
    this.store.dispatch({ type: 'SET_APPLICATION_LANGUAGE', payload: x });
  }

  setCustomerLogo(x) {
    const logo = {
      url: x
    };
    this.store.dispatch({
      type: 'SET_LOGO',
      payload: { data: logo, status: 200 }
    });
  }

  setClientColors(primaryHexColor = '#', secondaryHexColor = '#') {
    const color = {
      primary: primaryHexColor,
      secondary: secondaryHexColor
    };
    this.store.dispatch({
      type: 'SET_CLIENT_COLORS',
      payload: { data: color, status: 200 }
    });
  }

  setContextMenu(x) {
    this.store.dispatch({
      type: 'SET_CONTEXT_MENU',
      payload: { data: x, status: 200 }
    });
  }

  setWidgetEnabled(x) {
    this.store.dispatch({
      type: 'HAS_WIDGET_ENABLED',
      payload: { data: x, status: 200 }
    });
  }

  setCurrentApplication(x) {
    setCurrentApp(x);
    this.store.dispatch({
      type: 'SET_CURRENT_APPLICATION',
      payload: { data: x, status: 200 }
    });
  }

  setActiveGlobalMenuItem(x) {
    const state = this.store.getState();
    if (state.globalMenu.length > 0) {
      this.store.dispatch({
        type: 'SET_ACTIVE_GLOBAL_MENU_ITEM',
        payload: { data: x, status: 200 }
      });
    }
  }

  setGlobalMenu(x) {
    this.store.dispatch({
      type: 'SET_GLOBAL_MENU',
      payload: { data: x, status: 200 }
    });
  }

  getCommonHeaderSideNav(dispatch, isTokenRefresh) {
    getUserCommonHeaderData(dispatch, isTokenRefresh).then(
      (res) => {
        if (App.isValidResponseLayouts(res)) {
          this.callback(null, res);
        } else {
          this.callback(
            {
              status: 401,
              message: `We didnt get proper data from layouts / authorize api, response is ${JSON.stringify(res)}`,
              code: 'Invalid response'
            },
            null,
          );
        }
      },
      (err) => {
        this.callback(err, null);
      },
    );
  }

  constructor({
    language = getParamLanguage(getConfig('APPLICATION_LANGUAGE')),
    callback = () => {},
    desktopSidebarIsToggleable = false,
    intentionallyFail
  } = {}) {
    setIsUserToken();
    this.shouldFail = !!intentionallyFail;
    this.callback = callback;
    this.store = new Store(rootReducer);
    this.store.subscribe(this.render);
    this.store.dispatch({ type: 'INIT_EVENT' });
    this.setApplicationLanguage(language);
    this.headerNode = App.elementShouldExist('header#common-ui-jacket--header');
    this.sidebarNode = App.elementShouldExist(
      'aside#common-ui-jacket--sidebar',
    );
    this.footerNode = App.elementShouldExist('footer#common-ui-jacket--footer');
    this.appNode = App.elementShouldExist('div#common-ui-jacket--app');
    const handleSession = this.setHandleSessionExtend;
    const storeObj = this.store;
    if (desktopSidebarIsToggleable) {
      if (this.appNode) {
        this.appNode.classList.add(styles.desktopSidebarIsToggleable);
      }
    }
    if (this.sidebarNode) {
      this.sidebar = new Sidebar('aside#common-ui-jacket--sidebar', {
        desktopSidebarIsToggleable
      });
    }
    if (this.headerNode) {
      this.header = new Header('header#common-ui-jacket--header', {
        desktopSidebarIsToggleable
      });
    }
    if (this.footerNode) {
      this.footer = new Footer('footer#common-ui-jacket--footer', {
        desktopSidebarIsToggleable,
        callback,
        handleSession,
        storeObj
      });
      this.footerNode.classList.add(styles.enabled);
    }
  }

  render = (prevState, state, stateDiff) => {
    if (getIsCookieDisabled()) {
      displayCookieDisabledInfo();
      this.callback({}, {}, false, true);
    } else if (
      this.shouldFail &&
      App.hasApplicationLanguageUpdated(stateDiff)
    ) {
      this.callback({
        status: 404,
        message: 'Oh No!!! it "failed" 😉.'
      });
    } else if (
      App.hasApplicationLanguageUpdated(stateDiff) ||
      App.shouldUpdateRefreshAuthToken(stateDiff)
    ) {
      if (getIsUserToken()) {
        const { dispatch } = this.store;
        let isTokenRefresh = false;
        if (App.shouldUpdateRefreshAuthToken(stateDiff)) {
          isTokenRefresh = true;
        }
        this.getCommonHeaderSideNav(dispatch, isTokenRefresh);
      } else {
        getCommonHeaderData(this.store.dispatch).then(
          (res) => {
            if (App.isValidResponse(res)) {
              this.callback(null, res);
            } else {
              this.callback(
                {
                  status: 200,
                  message: `We didnt get propper data from layouts api, response is ${JSON.stringify(res)}`
                },
                null,
              );
            }
          },
          (err) => {
            this.callback(err, null);
          },
        );
      }
    }

    if (this.header && App.shouldUpdateTranslations(stateDiff)) {
      this.header.render();
      this.header.currentSelectedLanguage = state.applicationLanguage.replace(
        '-',
        '_',
      );
      this.header.translations = state.translations;
      this.header.hasGivenWidgetEnabled = state.hasGivenWidgetEnabled;
      this.header.widget = state.hasGivenWidgetEnabled;
      this.header.isEclientLink = getIsEclientToken();
      this.headerNode.classList.add(styles.enabled);
    }

    if (this.header && App.shouldUpdateLogo(stateDiff)) {
      this.header.logo = state.logo.url;
    }

    if (this.header && this.sidebar && App.shouldUpdateGlobalMenu(stateDiff)) {
      const { children = [{ url: '/' }] } = state.globalMenu[0];
      this.header.logolink = children[0].url;
    }

    if (this.header && this.sidebar && App.shouldUpdateGlobalMenu(stateDiff)) {
      const { children = [{ url: '/' }] } = state.globalMenu[0];
      this.header.logolink = children[0].url;
    }

    if (this.header && App.shouldUpdateActivity(stateDiff)) {
      this.header.activity = state.activity;
    }

    if (this.header && App.shouldUpdateCartLink(stateDiff)) {
      this.header.cartLink = state.cartLink;
    }

    if (this.header && App.shouldUpdateCatalogLink(stateDiff)) {
      this.header.catalogLink = state.catalogLink;
    }

    if (this.header && App.shouldUpdateTasksCount(stateDiff)) {
      this.header.tasksCount = state.tasksCount.currentTasksCount;
    }

    if (this.header && App.shouldUpdateBank(stateDiff)) {
      this.header.banks = state.banks;
    }

    if (this.header && App.shouldUpdateCustomerName(stateDiff)) {
      this.header.customerName = state.customerName;
    }

    if (this.header && App.shouldUpdateAwards(stateDiff)) {
      this.header.awards = state.awards;
    }

    if (this.header && App.shouldUpdateCatalogs(stateDiff)) {
      this.header.catalogs = state.catalogs;
    }

    if (this.header && App.shouldUpdateAlertsCount(stateDiff)) {
      this.header.alertsCount = state.alertsCount;
    }

    if (this.header && App.shouldUpdateGenericAlerts(stateDiff)) {
      this.header.genericAlerts = state.genericAlerts;
    }

    if (this.header && App.shouldUpdateRecieveAlerts(stateDiff)) {
      this.header.receiveAlerts = state.receiveAlerts;
    }

    if (this.header && App.shouldUpdateGreeting(stateDiff)) {
      this.header.greeting = state.greeting;
    }

    if (this.sidebar && App.shouldUpdateGreeting(stateDiff)) {
      this.sidebar.greeting = state.greeting;
    }

    if (this.footer && App.shouldUpdateSessionExtendDialog(stateDiff)) {
      if (getIsEclientToken()) {
        this.footer.logoutLink = getConfig('ECLIENT_LINK_LOGOUT');
      } else {
        const { logoutLink = '' } = state.greeting;
        this.footer.logoutLink = logoutLink;
      }
      this.footer.sessionExtendDialog = state.sessionExtendDialog;
    }

    if (App.shouldUpdateClientColors(stateDiff)) {
      setStyleTag(state.clientColors, hexToRgb(state.clientColors));
    }
    if (
      this.sidebar &&
      (App.shouldUpdateGlobalMenu(stateDiff) ||
        App.hasCurrentApplicationUpdated(stateDiff))
    ) {
      this.sidebar.render();
      this.sidebarNode.classList.add(styles.enabled);
      this.sidebar.isEclientLink = getIsEclientToken();
      this.sidebar.globalMenu = state.globalMenu;
      this.appNode.classList.add(styles.enabled);

      if (state.contextMenu && state.contextMenu.length > 0) {
        const x = state.contextMenu[0];
        if (x.id === reportingId) {
          const currentItem = x;
          if (currentItem.children) {
            currentItem.children.forEach((childObj) => {
              if (childObj.id === state.currentApplication) {
                const childItem = childObj;
                childItem.isCurrent = true;
              }
            });
          }
        }
      }
    }
    if (this.footer && App.shouldUpdateFooter(stateDiff)) {
      this.footer.render();
      this.footer.isEclientLink = getIsEclientToken();
      this.footer.translations = state.translations;
      this.footer.currentSelectedLanguage = state.applicationLanguage.replace(
        '-',
        '_',
      );
      this.footer.footerLinks = state.footer;
      this.appNode.classList.add(styles.enabled);
    }
    if (
      this.sidebar &&
      this.header &&
      App.isValidResponse({
        logo: { url: state.logo },
        subscriptions: state.globalMenu,
        color: state.clientColors
      }) &&
      (App.shouldUpdateContextMenu(stateDiff) ||
        (state.contextMenu && state.contextMenu.length > 0))
    ) {
      this.sidebar.contextMenu = state.contextMenu;
      this.header.setToggleSidebar(this.sidebar.toggleOpen);
    }
  };
}

export { App as Create, setConfig };
